// Design System Skeleton Component
// Loading placeholder with shimmer effect

import React from 'react';
import { Skeleton as MuiSkeleton, Box, styled } from '@mui/material';
import PropTypes from 'prop-types';
import { spacingTokens, borderTokens } from '../../tokens';

// Styled skeleton with shimmer effect
const StyledSkeleton = styled(MuiSkeleton)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.08)'
    : 'rgba(0, 0, 0, 0.08)',
  
  '&::after': {
    background: `linear-gradient(90deg, transparent, ${
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)'
    }, transparent)`,
  },
}));

/**
 * Skeleton - Loading placeholder component
 * 
 * Features:
 * - Text, circular, rectangular, and rounded variants
 * - Custom width and height
 * - Animation control
 * - Composite skeletons for complex layouts
 * 
 * @example
 * // Text skeleton
 * <Skeleton variant="text" width={200} />
 * 
 * // Avatar skeleton
 * <Skeleton variant="circular" width={40} height={40} />
 * 
 * // Card skeleton
 * <Skeleton variant="rectangular" height={200} />
 */
export const Skeleton = ({
  variant = 'text',
  width,
  height,
  animation = 'pulse',
  ...props
}) => {
  return (
    <StyledSkeleton
      variant={variant}
      width={width}
      height={height}
      animation={animation}
      {...props}
    />
  );
};

Skeleton.propTypes = {
  variant: PropTypes.oneOf(['text', 'circular', 'rectangular', 'rounded']),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  animation: PropTypes.oneOf(['pulse', 'wave', false]),
};

/**
 * CardSkeleton - Skeleton for card layouts
 */
export const CardSkeleton = ({ showActions = true }) => (
  <Box sx={{ p: 2, borderRadius: borderTokens.lg, bgcolor: 'background.paper' }}>
    <Box display="flex" alignItems="center" mb={2}>
      <Skeleton variant="circular" width={40} height={40} />
      <Box ml={2} flex={1}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="40%" />
      </Box>
    </Box>
    <Skeleton variant="rectangular" height={120} />
    <Box mt={2}>
      <Skeleton variant="text" />
      <Skeleton variant="text" width="80%" />
    </Box>
    {showActions && (
      <Box display="flex" gap={1} mt={2}>
        <Skeleton variant="rectangular" width={80} height={36} />
        <Skeleton variant="rectangular" width={80} height={36} />
      </Box>
    )}
  </Box>
);

/**
 * ListSkeleton - Skeleton for list items
 */
export const ListSkeleton = ({ count = 3, variant = 'default', showSelection = false }) => (
  <Box>
    {Array.from({ length: count }).map((_, index) => (
      <Box key={index} display="flex" alignItems="center" p={2}>
        {showSelection && (
          <Box mr={2}>
            <Skeleton variant="rectangular" width={20} height={20} />
          </Box>
        )}
        <Skeleton variant="circular" width={48} height={48} />
        <Box ml={2} flex={1}>
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="50%" />
          {variant === 'detailed' && (
            <Box mt={1}>
              <Skeleton variant="text" width="40%" />
            </Box>
          )}
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
          <Skeleton variant="rectangular" width={80} height={24} />
          {variant === 'detailed' && (
            <Skeleton variant="rectangular" width={24} height={24} />
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

ListSkeleton.propTypes = {
  count: PropTypes.number,
  variant: PropTypes.oneOf(['default', 'detailed']),
  showSelection: PropTypes.bool,
};

/**
 * TableSkeleton - Skeleton for table layouts
 */
export const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <Box>
    {/* Header */}
    <Box display="flex" p={2} borderBottom={1} borderColor="divider">
      {Array.from({ length: columns }).map((_, index) => (
        <Box key={index} flex={1} px={1}>
          <Skeleton variant="text" />
        </Box>
      ))}
    </Box>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <Box key={rowIndex} display="flex" p={2} borderBottom={1} borderColor="divider">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <Box key={colIndex} flex={1} px={1}>
            <Skeleton variant="text" width={colIndex === 0 ? '80%' : '60%'} />
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

TableSkeleton.propTypes = {
  rows: PropTypes.number,
  columns: PropTypes.number,
};

/**
 * GridSkeleton - Skeleton for grid layouts
 */
export const GridSkeleton = ({ count = 8, columns = 4 }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: `repeat(${Math.min(columns, 3)}, 1fr)`,
        lg: `repeat(${columns}, 1fr)`,
      },
      gap: 2,
      p: 2,
    }}
  >
    {Array.from({ length: count }).map((_, index) => (
      <Box
        key={index}
        sx={{
          p: 2,
          borderRadius: borderTokens.lg,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box ml={2} flex={1}>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="60%" />
          </Box>
        </Box>
        <Skeleton variant="rectangular" height={100} />
        <Box mt={2}>
          <Skeleton variant="text" />
          <Skeleton variant="text" width="70%" />
        </Box>
      </Box>
    ))}
  </Box>
);

GridSkeleton.propTypes = {
  count: PropTypes.number,
  columns: PropTypes.number,
};

/**
 * CompactListSkeleton - Skeleton for compact list layouts
 */
export const CompactListSkeleton = ({ count = 5 }) => (
  <Box>
    {Array.from({ length: count }).map((_, index) => (
      <Box
        key={index}
        display="flex"
        alignItems="center"
        p={1.5}
        borderBottom={index < count - 1 ? '1px solid' : 'none'}
        borderColor="divider"
      >
        <Skeleton variant="circular" width={32} height={32} />
        <Box ml={2} flex={1}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </Box>
        <Skeleton variant="rectangular" width={60} height={20} />
      </Box>
    ))}
  </Box>
);

CompactListSkeleton.propTypes = {
  count: PropTypes.number,
};

/**
 * DetailedListSkeleton - Skeleton for detailed list items with multiple data points
 */
export const DetailedListSkeleton = ({ count = 3, showActions = true }) => (
  <Box>
    {Array.from({ length: count }).map((_, index) => (
      <Box key={index} p={3} borderBottom="1px solid" borderColor="divider">
        {/* Header */}
        <Box display="flex" alignItems="flex-start" mb={2}>
          <Skeleton variant="circular" width={56} height={56} />
          <Box ml={2} flex={1}>
            <Skeleton variant="text" width="70%" sx={{ fontSize: '1.25rem' }} />
            <Skeleton variant="text" width="50%" />
            <Box display="flex" gap={1} mt={1}>
              <Skeleton variant="rectangular" width={60} height={24} />
              <Skeleton variant="rectangular" width={80} height={24} />
            </Box>
          </Box>
          {showActions && (
            <Skeleton variant="circular" width={32} height={32} />
          )}
        </Box>
        
        {/* Content */}
        <Box mb={2}>
          <Skeleton variant="text" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
        </Box>
        
        {/* Footer */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" gap={2}>
            <Skeleton variant="text" width={80} />
            <Skeleton variant="text" width={100} />
          </Box>
          <Box display="flex" gap={1}>
            <Skeleton variant="rectangular" width={80} height={32} />
            <Skeleton variant="rectangular" width={80} height={32} />
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

DetailedListSkeleton.propTypes = {
  count: PropTypes.number,
  showActions: PropTypes.bool,
};

/**
 * KanbanSkeleton - Skeleton for kanban board layouts
 */
export const KanbanSkeleton = ({ columns = 3, cardsPerColumn = 4 }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: 3,
      p: 3,
    }}
  >
    {Array.from({ length: columns }).map((_, colIndex) => (
      <Box key={colIndex}>
        {/* Column header */}
        <Box mb={2}>
          <Skeleton variant="text" width="60%" sx={{ fontSize: '1.1rem' }} />
          <Skeleton variant="text" width="40%" />
        </Box>
        
        {/* Cards */}
        <Box display="flex" flexDirection="column" gap={2}>
          {Array.from({ length: cardsPerColumn }).map((_, cardIndex) => (
            <Box
              key={cardIndex}
              sx={{
                p: 2,
                borderRadius: borderTokens.lg,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Skeleton variant="rectangular" width={60} height={20} />
                <Skeleton variant="circular" width={24} height={24} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

KanbanSkeleton.propTypes = {
  columns: PropTypes.number,
  cardsPerColumn: PropTypes.number,
};

export default Skeleton;